import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useContent } from '../contexts/ContentContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import { landingTranslations } from '../translations/landingTranslations';
import './Navbar.css';

interface NavbarProps {
  onScrollToSection?: (sectionId: string) => void;
  isScrolled?: boolean;
  isScrolling?: boolean;
  activeSection?: string;
}

const Navbar: React.FC<NavbarProps> = ({ onScrollToSection, isScrolled = false, isScrolling = false, activeSection = 'hero' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentLanguage } = useLanguage();
  const { websiteSettings } = useContent();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const lastClickTimeRef = useRef<number>(0);
  const CLICK_DEBOUNCE_DELAY = 150; // 150ms debounce - faster response
  
  // Get translations for current language
  const t = landingTranslations[currentLanguage] || landingTranslations.id;
  
  const isHomePage = location.pathname === '/';
  
  const handleNavClick = (sectionId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Debounce mechanism to prevent rapid clicks
    const now = Date.now();
    if (now - lastClickTimeRef.current < CLICK_DEBOUNCE_DELAY) {
      console.log('Click debounced for:', sectionId);
      return;
    }
    lastClickTimeRef.current = now;
    
    // Close mobile menu immediately
    setIsMobileMenuOpen(false);
    
    console.log('Navbar click handling:', sectionId, 'isHomePage:', isHomePage);
    
    if (isHomePage && onScrollToSection) {
      onScrollToSection(sectionId);
    } else {
      // Navigate to home page first, then scroll to section after navigation completes
      navigate('/', { replace: false });
      // Use setTimeout to allow navigation to complete before scrolling
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const headerOffset = 80; // Account for fixed navbar
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  };
  
  const handleMapClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const now = Date.now();
    if (now - lastClickTimeRef.current < CLICK_DEBOUNCE_DELAY) {
      console.log('Map click debounced');
      return;
    }
    lastClickTimeRef.current = now;
    
    setIsMobileMenuOpen(false);
    navigate('/peta-potensi');
  };
  
  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const now = Date.now();
    if (now - lastClickTimeRef.current < CLICK_DEBOUNCE_DELAY) {
      console.log('Contact click debounced');
      return;
    }
    lastClickTimeRef.current = now;
    
    setIsMobileMenuOpen(false);
    navigate('/contact');
  };
  
  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const now = Date.now();
    if (now - lastClickTimeRef.current < CLICK_DEBOUNCE_DELAY) {
      console.log('Logo click debounced');
      return;
    }
    lastClickTimeRef.current = now;
    
    setIsMobileMenuOpen(false);
    
    console.log('Logo click handling - scroll to hero');
    
    if (isHomePage && onScrollToSection) {
      onScrollToSection('hero');
    } else {
      // Navigate to home page first, then scroll to top after navigation completes
      navigate('/', { replace: false });
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);
    }
  };
  
  // Handle mobile menu toggle
  const handleMobileMenuToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const now = Date.now();
    if (now - lastClickTimeRef.current < CLICK_DEBOUNCE_DELAY) {
      console.log('Mobile menu toggle debounced');
      return;
    }
    lastClickTimeRef.current = now;
    
    setIsMobileMenuOpen(prev => !prev);
  };
  
  // Note: Removed hash-based navigation to prevent auto-triggering loops
  // Navigation now uses direct programmatic scrolling after page navigation

  // Handle navbar container clicks to prevent unwanted propagation
  const handleNavbarClick = (e: React.MouseEvent) => {
    // Only prevent default if clicking on the navbar itself, not on interactive elements
    if (e.target === e.currentTarget) {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  
  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="navbar-container" onClick={handleNavbarClick}>
        <div className="navbar-brand" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
          <img src={websiteSettings?.logo || "/logo192.png"} alt={websiteSettings?.siteName || "RIS Logo"} className="logo" />
        </div>
        <div className="navbar-menu">
          <a 
            href="#profile" 
            onClick={(e) => handleNavClick('profile', e)} 
            className={activeSection === 'profile' ? 'active' : ''}
          >
            {t.navbar.profile}
          </a>
          <a 
            href="#facilities" 
            onClick={(e) => handleNavClick('facilities', e)} 
            className={activeSection === 'facilities' ? 'active' : ''}
          >
            {t.navbar.facilities}
          </a>
          <a 
            href="#opportunities" 
            onClick={(e) => handleNavClick('opportunities', e)} 
            className={activeSection === 'opportunities' ? 'active' : ''}
          >
            {t.opportunities.title}
          </a>
          <a 
            href="#investment-opportunities" 
            onClick={(e) => handleNavClick('investment-opportunities', e)} 
            className={activeSection === 'investment-opportunities' ? 'active' : ''}
          >
            {t.investmentOpportunities.title}
          </a>
          <a href="#" onClick={handleMapClick}>
            {t.navbar.potentialMap}
          </a>
          <a href="#" onClick={handleContactClick}>
            {t.navbar.contact}
          </a>
          <div className="navbar-language">
            <LanguageSwitcher />
          </div>
        </div>
        <div className="mobile-menu-button">
          <button 
            onClick={handleMobileMenuToggle}
            className="mobile-menu-toggle"
            type="button"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <a 
          href="#profile" 
          onClick={(e) => handleNavClick('profile', e)}
          className={activeSection === 'profile' ? 'active' : ''}
        >
          {t.navbar.profile}
        </a>
        <a 
          href="#facilities" 
          onClick={(e) => handleNavClick('facilities', e)}
          className={activeSection === 'facilities' ? 'active' : ''}
        >
          {t.navbar.facilities}
        </a>
        <a 
          href="#opportunities" 
          onClick={(e) => handleNavClick('opportunities', e)}
          className={activeSection === 'opportunities' ? 'active' : ''}
        >
          {t.opportunities.title}
        </a>
        <a 
          href="#investment-opportunities" 
          onClick={(e) => handleNavClick('investment-opportunities', e)}
          className={activeSection === 'investment-opportunities' ? 'active' : ''}
        >
          {t.investmentOpportunities.title}
        </a>
        <a href="#" onClick={handleMapClick}>
          {t.navbar.potentialMap}
        </a>
        <a href="#" onClick={handleContactClick}>
          {t.navbar.contact}
        </a>
        <div className="mobile-menu-language">
          <LanguageSwitcher />
        </div>
      </div>
    </nav>
  );
};

export default Navbar;